import {useEffect, useMemo, useState} from 'react';

import {makeGetMedicalReport} from 'main/factories/usecases';
import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {ExamRequestResultTypeEnum} from 'domain/usecases/private/exam-results/get-exam-requests';

import {UseMedicalReportItemProps} from './types';
import {useExamResultOperationStore} from '../../store/useExamResultOperationStore';
import {useExamReportsStore} from '../../store/useExamReportsStore';

export const useMedicalReportItem = ({medicalReport, onLinkCopied}: UseMedicalReportItemProps) => {
	const [clickedBtnLoading, setClickedBtnLoading] = useState(false);
	const examRequestData = useExamReportsStore(state => state.examRequestData);
	const operationState = useExamResultOperationStore(state => state.examResultOperationState);
	const setOperationState = useExamResultOperationStore(
		state => state.setExamResultOperationState,
	);
	const resetOperationState = useExamResultOperationStore(
		state => state.resetExamResultOperationState,
	);

	const urlPacsmobile = medicalReport?.urlPacsmobile;

	const medicalReportParams = useMemo(
		() =>
			medicalReport
				? {
						cdAtendimento: medicalReport.cdAtendimento,
						cdOcorrencia: medicalReport.cdOcorrencia,
						flTipoExame: medicalReport.flTipoExame,
						cdOrdem: medicalReport.cdOrdem,
						flFormato: 'link',
						flOrigem: medicalReport.flOrigem,
					}
				: undefined,
		[medicalReport],
	);

	const openImageUrl = async () => {
		if (urlPacsmobile) {
			window.open(urlPacsmobile);
		}
	};

	const openPdf = async (isShare?: boolean) => {
		if (clickedBtnLoading) return;

		setOperationState({
			...operationState,
			loading: true,
			error: false,
			errorType: 'none',
			isShareAction: !!isShare,
			medicalReport,
		});
		setClickedBtnLoading(true);

		if (!medicalReportParams) {
			setOperationState({
				...operationState,
				loading: false,
				error: true,
				errorType: 'notFound',
			});
			setClickedBtnLoading(false);
			return;
		}

		try {
			const response = await makeGetMedicalReport().get(medicalReportParams);

			setOperationState({...operationState, loading: false});
			setClickedBtnLoading(false);

			if (isShare && response.url) {
				const responseBlob = await fetch(response.url);

				const blob = await responseBlob.blob();
				const hash = Array.from(crypto.getRandomValues(new Uint8Array(3)), byte =>
					byte.toString(16).padStart(2, '0'),
				).join('');
				const fileName = `Exame_${hash}.pdf`;
				const file = new File([blob], fileName, {type: 'application/pdf'});

				if (navigator.canShare && navigator.canShare({files: [file]})) {
					try {
						await navigator.share({
							files: [file],
						});
					} catch {
						return;
					}
				} else {
					await navigator.clipboard.writeText(response.url);
					onLinkCopied?.();
				}
				return;
			}

			window.open(response.url);
		} catch (error) {
			if (error instanceof APIError && error.statusCode === HttpStatusCode.notFound) {
				setOperationState({
					...operationState,
					loading: false,
					error: true,
					errorType: 'notFound',
				});
			} else if (error instanceof APIError && error.statusCode === HttpStatusCode.noContent) {
				setOperationState({
					...operationState,
					loading: false,
					error: true,
					errorType: 'noContent',
				});
			} else {
				setOperationState({
					...operationState,
					loading: false,
					error: true,
					errorType: 'generic',
				});
			}
			setClickedBtnLoading(false);
		} finally {
			setTimeout(() => {
				setClickedBtnLoading(false);
			}, 10000);
		}
	};

	const showExamReportItemActions =
		examRequestData?.dsTipoExame !== ExamRequestResultTypeEnum.LABORATORY &&
		!!medicalReport?.dtLaudo;

	const disableButtons = operationState.loading;

	const downloadButtonIsLoading =
		!operationState.isShareAction &&
		operationState.loading &&
		operationState.medicalReport === medicalReport;
	const shareButtonIsLoading =
		operationState.isShareAction &&
		operationState.loading &&
		operationState.medicalReport === medicalReport;

	useEffect(() => {
		if (operationState.error) {
			setTimeout(() => {
				resetOperationState();
			}, 4000);
		}
	}, [operationState]);

	return {
		clickedBtnLoading,
		setClickedBtnLoading,
		openImageUrl,
		openPdf,
		urlPacsmobile,
		operationState,
		showExamReportItemActions,
		disableButtons,
		downloadButtonIsLoading,
		shareButtonIsLoading,
	};
};
